import { Card, Box, Alert, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem, Tooltip, IconButton, Chip, Switch } from "@mui/material";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { updateScreenTitle, showSuccessMessage, showLoader, dismissLoader } from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import WDBox from "../../../../MaterialComponents/WDBox";
import partnerAPI from "../../../../services/channelPartnerService";
import AppService from "../../../../Configurations/Appservice";
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, CheckCircle as CheckCircleIcon, Cancel as CancelIcon } from "@mui/icons-material";
import DataTable from "../../../../Components/Table/DataTable";
import moment from "moment";
import TableLoader from "../../../../Components/AppLoader/TableLoader";

const ChannelPartnerUsersList = () => {
  const dispatch = useDispatch();
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingVerification, setUpdatingVerification] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    userName: "",
    password: "",
    email: "",
    phone: "",
    roleCode: "CHANNEL_PARTNER_USER"
  });

  const [tableData, setTableData] = useState({
    rows: [],
  });

  useEffect(() => {
    dispatch(updateScreenTitle("Channel Partner Users"));
    fetchPartners();
    return () => dispatch(updateScreenTitle(""));
  }, []);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      console.log("[ChannelPartnerUsers] Fetching channel partners...");
      const response = await partnerAPI.get("/api/channel-partners/onboarding/all");
      console.log("[ChannelPartnerUsers] Response:", response);

      const partnersList = Array.isArray(response.data) ? response.data : [];
      console.log("[ChannelPartnerUsers] Partners list:", partnersList);

      setPartners(partnersList);
      if (partnersList.length > 0) {
        console.log("[ChannelPartnerUsers] Setting first partner as selected:", partnersList[0]);
        setSelectedPartner(partnersList[0]);
        fetchUsers(partnersList[0].id);
      } else {
        console.warn("[ChannelPartnerUsers] No channel partners found");
      }
    } catch (error) {
      console.error("[ChannelPartnerUsers] Error fetching partners:", error);
      dispatch(showSuccessMessage("Error loading channel partners. Please try again."));
      setPartners([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = useCallback(async (partnerId) => {
    try {
      setLoading(true);
      const response = await partnerAPI.get(`/api/channel-partners/${partnerId}/users`);
      const usersList = Array.isArray(response.data) ? response.data : [];

      const rows = usersList.map((user) => ({
        ...user,
        roleCode: user.roles && user.roles.length > 0 ? user.roles[0].code : "USER",
      }));

      setTableData({ rows });
      setUsers(usersList);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
      setTableData({ rows: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  const handlePartnerChange = (partnerId) => {
    const partner = partners.find(p => p.id === partnerId);
    setSelectedPartner(partner);
    fetchUsers(partnerId);
  };

  const handleVerificationToggle = useCallback(async (userId, currentValue) => {
    if (!selectedPartner) return;
    
    setUpdatingVerification(userId);
    try {
      await AppService.put(`v1/user/${userId}/verification`, null, {
        params: { verified: !currentValue }
      });
      // Refresh the table data
      await fetchUsers(selectedPartner.id);
    } catch (error) {
      console.error("Error updating verification status:", error);
      dispatch(showSuccessMessage("Error updating verification status"));
    } finally {
      setUpdatingVerification(null);
    }
  }, [selectedPartner, fetchUsers, dispatch]);

  const handleToggleUserStatus = useCallback(async (userId, currentStatus) => {
    const action = currentStatus ? "deactivate" : "activate";
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }

    if (!selectedPartner) {
      dispatch(showSuccessMessage("Please select a channel partner"));
      return;
    }

    dispatch(showLoader());
    try {
      const endpoint = currentStatus
        ? `/api/channel-partners/${selectedPartner.id}/users/${userId}/deactivate`
        : `/api/channel-partners/${selectedPartner.id}/users/${userId}/activate`;

      await partnerAPI.post(endpoint);
      dispatch(showSuccessMessage(`User ${action}d successfully!`));
      await fetchUsers(selectedPartner.id);
    } catch (error) {
      console.error(`[ChannelPartnerUsers] Error ${action}ing user:`, error);
      dispatch(showSuccessMessage(error.response?.data?.message || `Error ${action}ing user`));
    } finally {
      dispatch(dismissLoader());
    }
  }, [selectedPartner, dispatch, fetchUsers]);

  const handleAddUser = () => {
    setFormData({
      name: "",
      userName: "",
      password: "",
      email: "",
      phone: "",
      roleCode: "CHANNEL_PARTNER_USER"
    });
    setAddDialogOpen(true);
  };

  const handleSaveUser = async () => {
    if (!formData.name || !formData.userName || !formData.password || !formData.email || !formData.phone) {
      dispatch(showSuccessMessage("Please fill all required fields"));
      return;
    }

    if (!selectedPartner) {
      dispatch(showSuccessMessage("Please select a channel partner"));
      return;
    }

    dispatch(showLoader());
    try {
      await partnerAPI.post(
        `/api/channel-partners/${selectedPartner.id}/users`,
        formData
      );
      dispatch(showSuccessMessage("User added successfully!"));
      setAddDialogOpen(false);
      fetchUsers(selectedPartner.id);
    } catch (error) {
      console.error("Error adding user:", error);
      dispatch(showSuccessMessage(error.response?.data?.message || "Error adding user"));
    } finally {
      dispatch(dismissLoader());
    }
  };

  // Define columns with status toggle functionality
  const columns = useMemo(() => [
    { Header: "name", accessor: "name", width: "12%" },
    { Header: "username", accessor: "username", width: "12%" },
    { Header: "email", accessor: "email", width: "18%" },
    { Header: "phone", accessor: "phone", width: "10%" },
    { Header: "role", accessor: "roleCode", width: "10%" },
    {
      Header: "verified",
      accessor: "isVerified",
      width: "10%",
      Cell: ({ row }) => (
        <Tooltip title={row.original.isVerified ? "Verified - Click to unverify" : "Not verified - Click to verify"}>
          <Switch
            checked={row.original.isVerified || false}
            onChange={() => handleVerificationToggle(row.original.id, row.original.isVerified)}
            disabled={updatingVerification === row.original.id}
            size="small"
            color="success"
          />
        </Tooltip>
      ),
    },
    {
      Header: "status",
      accessor: "isActive",
      width: "13%",
      Cell: ({ row }) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title={row.original.isActive ? "Click to deactivate" : "Click to activate"}>
            <IconButton
              size="small"
              onClick={() => handleToggleUserStatus(row.original.id, row.original.isActive)}
              color={row.original.isActive ? "success" : "error"}
            >
              {row.original.isActive ? (
                <CheckCircleIcon fontSize="small" />
              ) : (
                <CancelIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Chip
            label={row.original.isActive ? "Active" : "Inactive"}
            size="small"
            color={row.original.isActive ? "success" : "error"}
            variant="outlined"
          />
        </Box>
      ),
    },
  ], [handleToggleUserStatus, handleVerificationToggle, updatingVerification]);

  if (loading && users.length === 0) {
    return (
      <WDBox py={1}>
        <Card>
          <TableLoader />
        </Card>
      </WDBox>
    );
  }

  return (
    <WDBox py={3}>
      <Card>
        <Box p={3}>
          <Box mb={3}>
            <FormControl fullWidth>
              <InputLabel>Select Channel Partner</InputLabel>
              <Select
                value={selectedPartner?.id || ""}
                onChange={(e) => handlePartnerChange(e.target.value)}
                label="Select Channel Partner"
                disabled={partners.length === 0}
              >
                {partners.length === 0 ? (
                  <MenuItem disabled>No active channel partners found</MenuItem>
                ) : (
                  partners.map((partner) => (
                    <MenuItem key={partner.id} value={partner.id}>
                      {partner.organisationName || "Unknown"}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <h3>Users ({users.length})</h3>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddUser}
              color="primary"
            >
              Add User
            </Button>
          </Box>

          {users.length === 0 ? (
            <Alert severity="info">No users added yet. Click "Add User" to create one.</Alert>
          ) : (
            <DataTable
              table={{
                columns: columns,
                rows: tableData.rows,
              }}
            />
          )}
        </Box>
      </Card>

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add User to Channel Partner</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            fullWidth
            label="Full Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Username"
            value={formData.userName}
            onChange={(e) => setFormData({ ...formData, userName: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Phone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.roleCode}
              onChange={(e) => setFormData({ ...formData, roleCode: e.target.value })}
              label="Role"
            >
              <MenuItem value="CHANNEL_PARTNER_ADMIN">Admin</MenuItem>
              <MenuItem value="CHANNEL_PARTNER_USER">User</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveUser} variant="contained" color="primary">
            Add User
          </Button>
        </DialogActions>
      </Dialog>
    </WDBox>
  );
};

export default ChannelPartnerUsersList;
